// src/components/ProtectedRoute.js - With Auth Bypass

// Single unified import from React
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Navigate, useLocation } from 'react-router-dom';
// Single unified import from MUI - removing unused components
import { alpha } from '@mui/material';
import { CONSTANTS } from '../utils/constants';
import { useAuth } from '../hooks/useAuth';
import LoadingOverlay from './common/LoadingOverlay';

// Set this to true to bypass authentication, false to use normal authentication
const BYPASS_AUTH = false;

/**
 * Protected Route component that requires authentication and optionally checks for specific permissions
 * 
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components to render if authenticated
 * @param {string} [props.requiredPermission] Optional permission required to access this route
 * @param {string} [props.redirectTo='/unauthorized'] Path to redirect to if not authenticated or lacking permission
 */
const ProtectedRoute = ({ 
  children, 
  requiredPermission = null,
  redirectTo = '/unauthorized'
}) => {
  const { isAuthenticated, loading, verifySession, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);
  const hasCheckedSession = useRef(false);
  const location = useLocation();
  
  useEffect(() => {
    // Skip all authentication checks if bypass is enabled
    if (BYPASS_AUTH) {
      return;
    }
    
    // Skip if we've already checked or if already authenticated
    if (hasCheckedSession.current || isAuthenticated) {
      return;
    }
    
    const checkSession = async () => {
      hasCheckedSession.current = true;
      
      // TEMPORARILY MODIFIED: Since we're no longer using session IDs
      // Simply redirect to login if not authenticated
      if (!isAuthenticated) {
        // Instead of going to UAT_EVENTUS_URL, redirect to /login
        window.location.href = '/login';
      }
      
      // The code below is no longer used since we disabled session IDs
      // Keeping it commented for when session functionality is restored
      /*
      // Get session_id from URL if present
      const sessionId = searchParams.get('session_id');
      const storedSessionId = localStorage.getItem('session_id');
      
      console.log("Session ID from URL:", sessionId);
      console.log("Session ID from localStorage:", storedSessionId);
      
      // If URL has session ID, verify it
      if (sessionId) {
        setVerifying(true);
        try {
          await verifySession(sessionId);
          setVerifying(false);
        } catch (err) {
          console.error("Error during session verification:", err);
          setVerifying(false);
          window.location.href = CONSTANTS.UAT_EVENTUS_URL;
        }
      }
      // If no URL session but localStorage has one, verify the stored session
      else if (storedSessionId) {
        setVerifying(true);
        try {
          await verifySession(storedSessionId);
          setVerifying(false);
        } catch (err) {
          console.error("Error during stored session verification:", err);
          setVerifying(false);
          window.location.href = CONSTANTS.UAT_EVENTUS_URL;
        }
      }
      // If no session ID available, redirect
      else {
        window.location.href = CONSTANTS.UAT_EVENTUS_URL;
      }
      */
    };
    
    checkSession();
  }, [searchParams, verifySession, isAuthenticated]);
  
  // If bypass is enabled, always render children regardless of auth state
  if (BYPASS_AUTH) {
    return children;
  }
  
  if (loading || verifying) {
    return <LoadingOverlay message="Checking authentication..." />;
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If a permission is required, check if the user has it
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={redirectTo} state={{ 
      from: location,
      error: `You don't have permission to access this page. Required permission: ${requiredPermission}`
    }} replace />;
  }
  
  return children;
};

export default ProtectedRoute;